import { useEffect, useRef, useState } from 'react';
import { IOptions } from 'utils/types';

import {
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Tooltip,
} from '@mui/material';

import {
  PlaceholderText,
  TruncatedInputLabel,
} from './CustomSelectMultiple.styles';

interface ICustomSelectMultipleProps {
  setValue: (value: (string | number)[]) => void;
  value: (string | number)[];
  options: IOptions[];
  label: string;
  name: string;
  id: string;
}

const CustomSelectMultiple = (props: ICustomSelectMultipleProps) => {
  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const selectedValues = event.target.value as any[];
    props.setValue(selectedValues);
  };

  const spanRef = useRef<HTMLLabelElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (spanRef.current) {
        setIsTruncated(
          spanRef.current.scrollWidth > spanRef.current.clientWidth,
        );
      }
    };
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [props.label]);

  return (
    <FormControl fullWidth variant="filled">
      <Tooltip
        title={isTruncated ? props.label : ''}
        arrow
        disableHoverListener={!isTruncated}
        placement="top"
      >
        <TruncatedInputLabel
          ref={spanRef}
          htmlFor={props.id}
          data-testid={`label-${props.name}`}
        >
          {props.label}
        </TruncatedInputLabel>
      </Tooltip>
      <TextField
        select
        variant="filled"
        size="small"
        hiddenLabel
        fullWidth
        data-testid={`select-${props.name}`}
        defaultValue={[]}
        slotProps={{
          select: {
            multiple: true,
            value: props.value,
            onChange: handleChange,
            MenuProps: {
              PaperProps: { style: { maxHeight: 300, minWidth: 0 } },
            },
            renderValue: (selected: unknown) => {
              const selectedValues = selected as (number | string)[];
              if (selectedValues.length === props.options.length) {
                return <PlaceholderText>Todas</PlaceholderText>;
              } else if (selectedValues.length === 1) {
                const option = props.options.find(
                  (o) => o.value === selectedValues[0],
                );
                return <PlaceholderText>{option?.label}</PlaceholderText>;
              } else if (selectedValues.length > 1) {
                return (
                  <PlaceholderText>{`${selectedValues.length} selecionadas`}</PlaceholderText>
                );
              } else {
                return <PlaceholderText>Todas</PlaceholderText>;
              }
            },
            startAdornment: props.value.length === 0 && (
              <PlaceholderText>Todas</PlaceholderText>
            ),
          },
        }}
      >
        {props.options.map((option: IOptions) => (
          <MenuItem key={option.label} value={option.value}>
            <Checkbox
              checked={props.value.includes(option.value)}
              size="small"
            />
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
      </TextField>
    </FormControl>
  );
};

export default CustomSelectMultiple;
